import * as React from 'react';
import { useLocation, useNavigationType } from 'react-router';

/**
 * Starts every new page at the top of the window.
 *
 * `BrowserRouter` swaps the page under the window without touching its scroll
 * position, so following a link from the bottom of one page landed you at the
 * same depth in the next. Mount this once, inside the router.
 *
 * Back and forward (`POP`) are left alone so the browser can put you back
 * where you were, and a `#hash` link is left to scroll to its own target.
 */
export function useScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  // Read through a ref, not listed as a dependency: only a new page should
  // scroll. A page that rewrites its own query string (the booking page
  // switching floors) is the same page, and must stay where the visitor is.
  const typeRef = React.useRef(navigationType);
  // Declared first, so it runs before the effect below on the same commit.
  React.useLayoutEffect(() => {
    typeRef.current = navigationType;
  }, [navigationType]);

  React.useLayoutEffect(() => {
    if (typeRef.current === 'POP' || hash) return;
    // 'instant' beats any smooth scroll-behavior: a new page should not glide.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);
}
